<script setup lang="ts">
import {reactive, ref, watch, computed, nextTick} from "vue";
import {Avatar, Button, Drawer, Label, Message, Textarea, ToggleSwitch} from "@/Components/Library";
import {useRouter} from "vue-router";
import {updateContact as apiUpdateContact} from "@/Services/contacts";
import {useMainStore} from "@/Stores/store";
import {useToast} from "primevue/usetoast";

const $store = useMainStore();
const router = useRouter();
const toast = useToast();

const props = defineProps<{user: any}>();
const model = defineModel();
const emit = defineEmits<{
    contactUpdated: [any];
    createConversation: [];
}>();

const defaultContactForm = {
    id: null,
    notes: null,
    preferences: {
        allow_adding_to_groups: null,
    },
};
const contactForm = reactive({...defaultContactForm});

const isInitialized = ref(false);

const email = computed(() => (props.user.to ? props.user.email : props.user.invitation?.email));

const updateContact = async () => {
    if (!isInitialized.value) {
        return;
    }

    contactForm.id = props.user.contact_id;

    await apiUpdateContact(contactForm);
    toast.add({severity: "success", summary: "Success", detail: "Contact updated successfully.", life: 3000});
    emit("contactUpdated", contactForm);
};

const handleHide = () => {
    $store.show.contact = null;
    $store.show.user = null;
    if (router.currentRoute.value.name === "contacts.show") {
        router.replace({name: "contacts.index"});
    }
    isInitialized.value = false;
};

watch(() => contactForm.preferences, updateContact, {deep: true});
watch(model, async (isOpen) => {
    if (isOpen) {
        isInitialized.value = false;
        contactForm.id = props.user.contact_id;
        contactForm.notes = props.user.notes ?? null;
        contactForm.preferences = {
            allow_adding_to_groups: props.user.preferences?.allow_adding_to_groups ?? null,
        };
        await nextTick();
        isInitialized.value = true;
    }
});
</script>
<template>
    <Drawer
        v-model:visible="model"
        header=" "
        position="bottom"
        @hide="handleHide"
        class="relative max-h-screen max-w-3xl overflow-y-auto"
    >
        <div class="flex flex-col gap-6 pb-12">
            <div class="flex flex-col items-center gap-4">
                <Avatar
                    :user="user"
                    class="h-16 w-16 text-3xl"
                    :class="[`bg-${user.profile?.color?.class ?? 'stone-300'}`]"
                    shape="circle"
                />
                <h2 class="text-xl font-bold sm:text-3xl">
                    {{ user.name }}
                </h2>
            </div>
            <div class="rounded-lg bg-stone-50 p-4 dark:bg-stone-900" v-if="email">
                <div class="flex flex-col gap-0.5">
                    <span class="text-sm">Email</span>
                    <span class="font-bold">{{ email }}</span>
                </div>
            </div>
            <Message v-if="user.to" severity="warn">This user has not yet accepted your invitation</Message>
            <div v-if="!user.to" class="flex flex-col gap-6">
                <div class="w-full px-4">
                    <Button
                        class="w-full bg-askr-green text-white hover:brightness-110"
                        @click="emit('createConversation')"
                    >
                        Start a chat
                    </Button>
                </div>
                <div class="flex flex-col rounded-lg bg-stone-50 p-4 dark:bg-stone-900">
                    <h3 class="mb-2 text-sm">Personal notes</h3>
                    <Textarea v-model="contactForm.notes" placeholder="Add notes" auto-resize fluid />
                    <span class="text-xs text-stone-500">
                        In accordance to GDPR your contact can request to see these notes.
                    </span>
                    <Button class="mt-2 w-full bg-askr-green text-white hover:brightness-110" @click="updateContact">
                        Save notes
                    </Button>
                </div>
                <div class="flex flex-col rounded-lg bg-stone-50 p-4 dark:bg-stone-900">
                    <h3 class="mb-2 text-sm">Preferences</h3>
                    <div class="flex items-center gap-2">
                        <Label for="allow_adding_to_groups">Allow this contact to add you to groups</Label>
                        <ToggleSwitch
                            class="flex-none"
                            id="allow_adding_to_groups"
                            name="allow_adding_to_groups"
                            v-model="contactForm.preferences.allow_adding_to_groups"
                        />
                    </div>
                </div>
                <div class="rounded-lg bg-stone-50 p-4 dark:bg-stone-900">
                    <h3 class="mb-2 text-sm">Groups you have in common</h3>
                    <div v-if="user.groups?.length" class="flex flex-col divide-y dark:divide-stone-700">
                        <template v-for="group in user.groups" :key="group.id">
                            <div class="flex items-center gap-2 px-2 py-4">
                                <Avatar
                                    :label="group.initials"
                                    class="mr--2 h-8 w-8"
                                    :class="[
                                        `bg-${group.color?.class}`,
                                        group?.color?.class === 'white' ? 'text-black' : 'text-white',
                                    ]"
                                />
                                <div class="font-bold">
                                    {{ group.name }}
                                </div>
                            </div>
                        </template>
                    </div>
                    <div v-else class="flex flex-col items-center gap-1 px-2 py-4">
                        <span>No groups in common yet</span>
                        <!--                        <Button text class="text-askr-green" :icon="{icon: 'lucide:plus', width: '24'}">-->
                        <!--                            Invite the user to a group-->
                        <!--                        </Button>-->
                    </div>
                </div>
            </div>
        </div>
    </Drawer>
</template>
<style scoped></style>
